import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import { EMPTY_FIELD } from 'support/helpers/const/const';
import { formatDayAndTime } from 'support/helpers/dateTime/dateTime';
import { isDefined } from 'support/helpers/generic/generic';
import { getOriginalPDF } from 'support/helpers/messages/messages';
import { isMessageV2DTO } from 'support/helpers/transactions/transactions';
import { MessageV2DTO, PartnerDTO, SenderEnvelopeDTO, TransactionOriginApp } from 'support/types';
import { DocumentEnvironmentBadge } from 'components/DocumentEnvironmentBadge/DocumentEnvironmentBadge';
import { Button } from 'components/Form/Button';
import { TradePartnerNameAndLogo } from 'components/TradePartnerNameAndLogo';
import { TransactionStatusDisplay } from 'components/TransactionStatusDisplay/TransactionStatusDisplay';

import { DocumentProperties, DocumentPropertiesItem } from '../../../../Sidebar/SidebarProperties/SidebarProperties';
import { LastEditBy } from './components/LastEditBy/LastEditBy';
import { hasProofOfDelivery, ProofOfDelivery } from './components/ProofOfDelivery/ProofOfDelivery';
import { useDocumentSource } from './useDocumentSource';
import { useIssueResolverName } from './useIssueResolverName';
import { WebEDIDocumentPropertiesKeys } from './WebEDIDocumentPropertiesKeys';

type WebEDIDocumentPropertiesProps = {
  partner: PartnerDTO | undefined | null;
  document: MessageV2DTO | SenderEnvelopeDTO | undefined;
  documentOriginApp: TransactionOriginApp;
  currentPartner: PartnerDTO | undefined;
  onViewPDF?: (url: string, filename: string) => void;
};

export const WebEDIDocumentProperties = ({
  partner,
  document,
  documentOriginApp,
  currentPartner,
  onViewPDF,
}: WebEDIDocumentPropertiesProps) => {
  const { t } = useTranslation();
  const isMessage = document && isMessageV2DTO(document);
  const isSenderEnvelope = document && !isMessageV2DTO(document);

  const issueResolverName = useIssueResolverName(
    isMessage
      ? { isMessage: true, document, currentPartner, partner }
      : { isMessage: false, document, currentPartner, partner },
  );

  const documentSourceValue = useDocumentSource({ document, documentOriginApp });
  const messageDocument = isMessage ? document : undefined;
  const issueResolverItem = getIssueResolverItem(issueResolverName, t);
  const messageFlowItem = getMessageFlowItem(messageDocument?.flow, t);
  const proofOfDeliveryItem = getProofOfDeliveryItem(messageDocument, t);
  const editInformation = getEditInformation(messageDocument?.latest_user_update, t);
  const documentSource = getDocumentSource(documentSourceValue, t);

  const originalPDF = getOriginalPDFItem({
    originalPDF: getOriginalPDF(documentOriginApp, messageDocument),
    onViewPDF,
    t,
  });

  const properties: Array<DocumentPropertiesItem | null> = [
    {
      label: t('webedi:sidebar.properties.partner'),
      key: WebEDIDocumentPropertiesKeys.PARTNER,
      value: (
        <TradePartnerNameAndLogo
          partner={partner}
          size="small"
          textConfiguration={{ className: 'text-inherit' }}
          logoConfiguration={{ className: 'rounded-full' }}
        />
      ),
    },
    {
      label: t('webedi:sidebar.properties.status'),
      key: WebEDIDocumentPropertiesKeys.STATUS,
      value: (
        <TransactionStatusDisplay
          transaction={isMessage ? document : undefined}
          state={isSenderEnvelope ? document?.state : undefined}
          currentPartner={currentPartner}
          originApp={documentOriginApp}
        />
      ),
    },
    issueResolverItem,
    messageFlowItem,
    {
      label: t('webedi:sidebar.properties.documentDate'),
      key: WebEDIDocumentPropertiesKeys.DOCUMENT_DATE,
      value: isMessage
        ? formatDayAndTime(document.created_at)
        : isSenderEnvelope
          ? formatDayAndTime(document.createdAt)
          : EMPTY_FIELD,
    },
    documentSource,
    originalPDF,
    editInformation,
    proofOfDeliveryItem,
  ];

  return <DocumentProperties properties={properties.filter(isDefined)} />;
};

const getIssueResolverItem = (
  issueResolverName: string | null | undefined,
  t: TFunction,
): DocumentPropertiesItem | null =>
  issueResolverName
    ? {
        label: t('webedi:sidebar.properties.issueResolver'),
        key: WebEDIDocumentPropertiesKeys.ISSUE_RESOLVER,
        value: issueResolverName,
      }
    : null;

const getMessageFlowItem = (flow: MessageV2DTO['flow'] | undefined, t: TFunction): DocumentPropertiesItem | null =>
  flow === 'TEST'
    ? {
        label: t('webedi:sidebar.properties.environment'),
        key: WebEDIDocumentPropertiesKeys.ENVIRONMENT,
        value: <DocumentEnvironmentBadge flow={flow} />,
      }
    : null;

const getProofOfDeliveryItem = (document: MessageV2DTO | undefined, t: TFunction): DocumentPropertiesItem | null =>
  document && hasProofOfDelivery(document)
    ? {
        label: t('webedi:proofOfDelivery.label'),
        key: WebEDIDocumentPropertiesKeys.PROOF_OF_DELIVERY,
        value: (
          <ProofOfDelivery
            receiverEnvelope={{
              proofOfDelivery: document.receiver_envelope.proofOfDelivery,
              receiverIntegration: document.receiver_envelope.receiverIntegration,
            }}
          />
        ),
      }
    : null;

const getEditInformation = (
  latestUpdate: MessageV2DTO['latest_user_update'],
  t: TFunction,
): DocumentPropertiesItem | null =>
  latestUpdate
    ? {
        label: t('webedi:sidebar.properties.editInformation.editedAt.label'),
        key: WebEDIDocumentPropertiesKeys.EDIT_INFORMATION,
        value: <LastEditBy latestUpdate={latestUpdate} />,
      }
    : null;

const getDocumentSource = (
  documentSourceValue: ReturnType<typeof useDocumentSource>,
  t: TFunction,
): DocumentPropertiesItem | null =>
  documentSourceValue
    ? {
        label: t('webedi:sidebar.properties.documentSource'),
        key: WebEDIDocumentPropertiesKeys.DOCUMENT_SOURCE,
        value: documentSourceValue,
      }
    : null;

const getOriginalPDFItem = ({
  originalPDF,
  onViewPDF,
  t,
}: {
  originalPDF: ReturnType<typeof getOriginalPDF>;
  onViewPDF: WebEDIDocumentPropertiesProps['onViewPDF'];
  t: TFunction;
}): DocumentPropertiesItem | null =>
  originalPDF && onViewPDF
    ? {
        label: t('webedi:sidebar.properties.originalPDF'),
        key: WebEDIDocumentPropertiesKeys.ORIGINAL_PDF,
        value: (
          <Button
            type="button"
            variant="secondary"
            size="extra-small"
            analyticsId="transactions:view_original_pdf"
            onClick={() => onViewPDF(originalPDF.url, originalPDF.filename)}
          >
            {t('webedi:sidebar.properties.viewPDF')}
          </Button>
        ),
      }
    : null;
